async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  // Only attach a JSON Content-Type when we actually have a body to send.
  // Setting Content-Type on bodyless DELETE/GET makes Fastify try to parse
  // an empty body as JSON and return "Bad Request".
  const headers: Record<string, string> = {
    ...(init.headers as Record<string, string> | undefined ?? {}),
  };
  if (init.body !== undefined && init.body !== null && !("Content-Type" in headers)) {
    headers["Content-Type"] = "application/json";
  }

  const method = (init.method ?? "GET").toUpperCase();

  let res: Response;
  try {
    res = await fetch(path, {
      ...init,
      credentials: "include",
      headers,
    });
  } catch (err) {
    // Network-level failure (server down, DNS, CORS, offline, etc.).
    // In dev mode, surface this loudly — generic "Failed to fetch" tells
    // the user nothing about whether the backend is reachable.
    const networkMessage =
      err instanceof Error ? err.message : "Network request failed";
    console.error(`[API] ${method} ${path} — network error:`, err);
    throw new ApiError(
      `Network error calling ${method} ${path}: ${networkMessage}`,
      0
    );
  }

  if (!res.ok) {
    let message = `HTTP ${res.status}`;
    let details: string | null = null;
    try {
      const data = await res.json();
      if (data?.error) message = data.error;
      if (data?.details) details = String(data.details);
    } catch {
      // body wasn't JSON — try plain text fallback so we still surface
      // *something* in dev mode instead of a generic "HTTP 500".
      try {
        const txt = await res.text();
        if (txt && txt.length > 0 && txt.length < 500) {
          details = txt;
        }
      } catch {
        // body was already consumed or unreadable
      }
    }
    // Always log full failures to console for debugging.
    console.error(
      `[API] ${method} ${path} — HTTP ${res.status}: ${message}` +
        (details ? `\n  details: ${details}` : "")
    );
    // Suffix the status code so toasts/alerts always include it without
    // every caller having to remember to print err.status.
    const fullMessage =
      res.status >= 400 ? `${message} (HTTP ${res.status})` : message;
    throw new ApiError(fullMessage, res.status, details);
  }

  const text = await res.text();
  return (text ? JSON.parse(text) : {}) as T;
}

export class ApiError extends Error {
  constructor(
    message: string,
    public status: number,
    public details: string | null = null
  ) {
    super(message);
    this.name = "ApiError";
  }
}

/**
 * Format an error from any catch block into a verbose string suitable for
 * an alert() or toast in dev mode. Always shows:
 *   - error message (always)
 *   - HTTP status code (if ApiError)
 *   - server stack snippet (if ApiError with details)
 *   - JS error name (if non-ApiError)
 *
 * Use this everywhere we surface errors to the user during dev. Once the
 * app goes live, swap to a quieter helper that only shows the message.
 */
export function formatErrorForAlert(err: unknown, context?: string): string {
  const prefix = context ? `${context}\n\n` : "";
  if (err instanceof ApiError) {
    let out = `${prefix}${err.message}`;
    if (err.details) {
      out += `\n\nServer details:\n${err.details}`;
    }
    return out;
  }
  if (err instanceof Error) {
    return `${prefix}${err.name}: ${err.message}`;
  }
  return `${prefix}Unknown error: ${String(err)}`;
}

// ===== Auth =====
export async function login(password: string): Promise<void> {
  await request<{ ok: true }>("/api/auth/login", {
    method: "POST",
    body: JSON.stringify({ password }),
  });
}

export async function logout(): Promise<void> {
  await request<{ ok: true }>("/api/auth/logout", { method: "POST" });
}

export async function checkSession(): Promise<boolean> {
  const data = await request<{ authenticated: boolean }>("/api/auth/session");
  return data.authenticated;
}

// ===== Clients =====
export interface ClientSummary {
  id: number;
  businessName: string;
  websiteUrl: string;
  pageCount: number;
  createdAt: string;
  // fix268/fix683: workspaceSection is retained only to detect the "inactive"
  // section. Placement is now driven by isPriority (Priority section) vs the
  // general "Clients" section. GBP and Content are independent per-client flags.
  workspaceSection: "content" | "gbp" | "other" | "inactive";
  isPriority: boolean;
  gbpChecked: boolean;
  contentChecked: boolean;
}

export async function listClients(): Promise<ClientSummary[]> {
  const data = await request<{ clients: ClientSummary[] }>("/api/clients");
  return data.clients;
}

// fix328: global generation pause controls.
export async function getGenerationPaused(): Promise<{ paused: boolean }> {
  return request(`/api/generation/pause`, { method: "GET" });
}
export async function pauseGeneration(): Promise<{ ok: true; paused: boolean }> {
  return request(`/api/generation/pause`, { method: "POST" });
}
export async function resumeGeneration(): Promise<{
  ok: true;
  paused: boolean;
  resumed: number;
}> {
  return request(`/api/generation/resume`, { method: "POST" });
}

// fix353: enable/disable an education subject (topicId) or whole category.
export async function setEducationDisabled(
  articleId: number,
  target: { topicId?: string; category?: string; disabled: boolean }
): Promise<{ ok: true; changed: number; disabled: boolean }> {
  return request(`/api/articles/${articleId}/education-disable`, {
    method: "POST",
    body: JSON.stringify(target),
  });
}

// fix346: pause/resume a single Dental Education item (not global).
export async function setEducationItemPaused(
  articleId: number,
  paused: boolean
): Promise<{ ok: true; paused: boolean }> {
  return request(`/api/articles/${articleId}/education-pause`, {
    method: "POST",
    body: JSON.stringify({ paused }),
  });
}

// fix335: regenerate a single attraction article immediately.
export async function regenerateAttraction(
  articleId: number,
  index: number
): Promise<{ ok: true; aiScore: number | null }> {
  return request(`/api/articles/${articleId}/attractions-regenerate`, {
    method: "POST",
    body: JSON.stringify({ index }),
  });
}

// fix329: regenerate a single education topic (high AI score, etc.).
export async function regenerateEducationTopic(
  articleId: number,
  topicId: string
): Promise<{ ok: true }> {
  return request(`/api/articles/${articleId}/education-regenerate`, {
    method: "POST",
    body: JSON.stringify({ topicId }),
  });
}

// fix309: set which Dental Education category batches are approved (or all).
export async function setEducationApproval(
  articleId: number,
  body: { approvedCategories?: string[]; approveAll?: boolean }
): Promise<{ ok: true }> {
  return request(`/api/articles/${articleId}/education-approval`, {
    method: "PATCH",
    body: JSON.stringify(body),
  });
}

// fix268: batch-update the workspace section for multiple clients at once.
// fix683: each update carries three independent booleans. gbpChecked and
// contentChecked are per-client tags (no placement effect); only isPriority
// moves the client into the Priority section.
export async function updateClientSections(
  updates: Array<{
    id: number;
    gbpChecked: boolean;
    contentChecked: boolean;
    isPriority: boolean;
  }>
): Promise<{ ok: true; updated: number }> {
  return request(`/api/clients/sections`, {
    method: "PATCH",
    body: JSON.stringify({ updates }),
  });
}

// fix268: bulk-import clients from parsed CSV rows. Imported clients land in the
// "Other" section.
export async function importClientsCsv(
  rows: Array<{ businessName: string; websiteUrl: string }>
): Promise<{
  ok: true;
  imported: number;
  skipped: number;
  clients: Array<{ id: number; businessName: string }>;
}> {
  return request(`/api/clients/import-csv`, {
    method: "POST",
    body: JSON.stringify({ rows }),
  });
}

export async function createClient(
  businessName: string,
  websiteUrl: string
): Promise<{ id: number }> {
  return request<{ id: number }>("/api/clients", {
    method: "POST",
    body: JSON.stringify({ businessName, websiteUrl }),
  });
}

export async function deleteClient(id: number): Promise<void> {
  await request<{ ok: true }>(`/api/clients/${id}`, { method: "DELETE" });
}

// fix582: move a client to the Inactive section (active:false) or back into
// rotation (active:true). Used by the "Move to Inactive" / "Reactivate" buttons.
export async function setClientActive(
  id: number,
  active: boolean
): Promise<{ ok: true; id: number; active: boolean }> {
  return request(`/api/clients/${id}/active`, {
    method: "PATCH",
    body: JSON.stringify({ active }),
  });
}

export interface Client extends ClientSummary {
  // Basic + Location
  businessAddress: string | null;
  // GBP
  gbpPrimaryCategory: string | null;
  gbpCid: string | null;
  // WordPress connection (Publish step)
  wpSiteUrl: string | null;
  wpUsername: string | null;
  wpAppPasswordSet: boolean;
  wpGeoCityTemplateSet: boolean;
  wpGeoNeighborhoodTemplateSet: boolean;
  wpGeoReferencePageId: number | null;
  gbpSecondaryCategories: string[] | null;
  gbpServices: Record<string, string[]> | null;
  // Voice & Tone
  voiceTones: string[] | null;
  pointOfView: string | null;
  writingModel: string | null;
  writingVoiceProfile: string | null;
  wordsToAlwaysUse: string | null;
  wordsToNeverUse: string | null;
  // Audience
  targetAudience: string | null;
  // About
  aboutBusiness: string | null;
  uniqueSellingPoints: string | null;
  credentials: string | null;
  // Reference
  onboardingNotes: string | null;
  specificRequests: string | null;
  writingSamples: string | null;
  // Brand & CTA
  brandPrimaryColor: string | null;
  brandAccentColor: string | null;
  phoneNumber: string | null;
  ctaText: string | null;
  socialProfiles: string | null;
  businessLogoUrl: string | null;
  // Reviews
  averageRating: number | null;
  totalReviewCount: number | null;
  reviewSourceUrl: string | null;
  // Local
  localDetails: string | null;
  // External links
  externalLinkDomains: string[] | null;
  excludedDomains: string[] | null;
  // Image preferences
  defaultImageStyle: string | null;
  brandStyleGuide: string | null;
}

export async function getClient(id: number): Promise<Client> {
  return request<Client>(`/api/clients/${id}`);
}

export interface UpdateClientPayload {
  businessName?: string;
  websiteUrl?: string;
  businessAddress?: string | null;
  gbpPrimaryCategory?: string | null;
  gbpCid?: string | null;
  wpSiteUrl?: string | null;
  wpUsername?: string | null;
  wpAppPassword?: string | null;
  wpGeoCityTemplate?: string | null;
  wpGeoNeighborhoodTemplate?: string | null;
  wpGeoReferencePageId?: number | null;
  gbpSecondaryCategories?: string[] | null;
  gbpServices?: Record<string, string[]> | null;
  voiceTones?: string[] | null;
  pointOfView?: string | null;
  writingModel?: string | null;
  writingVoiceProfile?: string | null;
  wordsToAlwaysUse?: string | null;
  wordsToNeverUse?: string | null;
  targetAudience?: string | null;
  aboutBusiness?: string | null;
  uniqueSellingPoints?: string | null;
  credentials?: string | null;
  onboardingNotes?: string | null;
  specificRequests?: string | null;
  writingSamples?: string | null;
  brandPrimaryColor?: string | null;
  brandAccentColor?: string | null;
  phoneNumber?: string | null;
  ctaText?: string | null;
  socialProfiles?: string | null;
  businessLogoUrl?: string | null;
  averageRating?: number | null;
  totalReviewCount?: number | null;
  reviewSourceUrl?: string | null;
  localDetails?: string | null;
  defaultImageStyle?: string | null;
  brandStyleGuide?: string | null;
}

export async function updateClient(
  id: number,
  payload: UpdateClientPayload
): Promise<{ ok: true }> {
  return request<{ ok: true }>(`/api/clients/${id}`, {
    method: "PATCH",
    body: JSON.stringify(payload),
  });
}

// fix707: best-effort auto-extract the GBP CID from the client's website.
export async function extractGbpCid(
  clientId: number
): Promise<{ cid: string | null; source: string | null; reason?: string }> {
  return request(`/api/clients/${clientId}/extract-gbp-cid`, {
    method: "POST",
    body: "{}",
  });
}

// fix710: WordPress Publish step.
export async function wpTestConnection(clientId: number): Promise<{
  ok: boolean;
  userId?: number;
  userName?: string;
  canEditPages?: boolean;
  error?: string;
}> {
  return request(`/api/clients/${clientId}/wp/test`, {
    method: "POST",
    body: "{}",
  });
}

export async function wpCreateTestDraft(clientId: number): Promise<{
  ok: boolean;
  pageId?: number;
  editLink?: string;
  error?: string;
}> {
  return request(`/api/clients/${clientId}/wp/test-draft`, {
    method: "POST",
    body: "{}",
  });
}

export async function wpBuildAreasBlock(
  clientId: number,
  opts?: { sitemapXml?: string; buttonColor?: string; parentPath?: string; textColor?: "black" | "white" }
): Promise<{
  ok: boolean;
  html?: string;
  areaCount?: number;
  mapSource?: string;
  error?: string;
  notes?: string[];
}> {
  return request(`/api/clients/${clientId}/wp/areas-block`, {
    method: "POST",
    body: JSON.stringify(opts ?? {}),
  });
}

export async function wpFetchPageTemplate(
  clientId: number,
  pageId: number
): Promise<{ ok: boolean; templateJson?: string; title?: string; error?: string }> {
  return request(`/api/clients/${clientId}/wp/fetch-template/${pageId}`);
}

export async function wpGetTemplates(clientId: number): Promise<{
  cityTemplate: string;
  neighborhoodTemplate: string;
}> {
  return request(`/api/clients/${clientId}/wp/templates`);
}

export async function wpInspectPage(
  clientId: number,
  pageId: number
): Promise<{
  ok: boolean;
  pageId?: number;
  title?: string;
  template?: string;
  meta?: Record<string, unknown>;
  taxonomies?: Record<string, number[]>;
  error?: string;
}> {
  return request(`/api/clients/${clientId}/wp/inspect/${pageId}`);
}

export interface WpPublishResult {
  articleId: number;
  title: string;
  ok: boolean;
  pageId?: number;
  editLink?: string;
  error?: string;
}

export async function wpPublishGeo(
  clientId: number,
  articleIds: number[]
): Promise<{ results: WpPublishResult[] }> {
  return request(`/api/clients/${clientId}/wp/publish-geo`, {
    method: "POST",
    body: JSON.stringify({ articleIds }),
  });
}

// ===== Target Audience auto-fill (chunk 3q) =====
export async function autofillTargetAudience(
  clientId: number,
  inputs: {
    practiceAddress: string;
    city: string;
    state: string;
    zip: string;
    services: string;
  }
): Promise<{ targetAudience: string; costCents: number }> {
  return request(`/api/clients/${clientId}/autofill-target-audience`, {
    method: "POST",
    body: JSON.stringify(inputs),
  });
}

export async function computeLandmarkDistance(
  clientId: number,
  landmark: string,
  city?: string
): Promise<{ distanceLabel: string | null; miles: number | null; bearing: string | null }> {
  return request(`/api/clients/${clientId}/landmark-distance`, {
    method: "POST",
    body: JSON.stringify({ landmark, city }),
  });
}

export async function autofillLocalDetails(
  clientId: number,
  inputs: {
    practiceAddress: string;
    city: string;
    state: string;
    zip: string;
    services: string;
  }
): Promise<{ localDetails: string; costCents: number }> {
  return request(`/api/clients/${clientId}/autofill-local-details`, {
    method: "POST",
    body: JSON.stringify(inputs),
  });
}

// ===== Content Gap manual URL overrides (chunk 3q) =====
/**
 * Per-client overrides for the Content Gap matcher.
 * Shape: { "<gbp service name>": { url: string, manuallySet: true } | null }
 *   - object value → user manually set this URL
 *   - null value   → user explicitly cleared (stays Missing on re-match)
 *   - missing key  → no override (use auto-match)
 */
export type ContentGapOverride =
  | { url: string; manuallySet: true }
  | { hidden: true }
  | null;

export async function getContentGapOverrides(
  clientId: number
): Promise<{ overrides: Record<string, ContentGapOverride> }> {
  return request(`/api/clients/${clientId}/content-gap-overrides`);
}

export async function setContentGapOverride(
  clientId: number,
  serviceName: string,
  url: string | null
): Promise<{ ok: true; override: ContentGapOverride }> {
  return request(
    `/api/clients/${clientId}/content-gap-overrides/${encodeURIComponent(
      serviceName
    )}`,
    {
      method: "PUT",
      body: JSON.stringify({ url }),
    }
  );
}

/**
 * Hide a service from Content Gap analysis. Used when the GBP entry isn't
 * actually a service (e.g., a promotional offer like "$89 Exam Special")
 * and shouldn't be tracked. Stored as `{hidden: true}` server-side; the
 * Research tab filters these out unless "Show hidden" is toggled.
 */
export async function hideContentGapService(
  clientId: number,
  serviceName: string
): Promise<{ ok: true; override: ContentGapOverride }> {
  return request(
    `/api/clients/${clientId}/content-gap-overrides/${encodeURIComponent(
      serviceName
    )}`,
    {
      method: "PUT",
      body: JSON.stringify({ hidden: true }),
    }
  );
}

export async function deleteContentGapOverride(
  clientId: number,
  serviceName: string
): Promise<{ ok: true }> {
  return request(
    `/api/clients/${clientId}/content-gap-overrides/${encodeURIComponent(
      serviceName
    )}`,
    { method: "DELETE" }
  );
}

// ===== AI helpers =====
export interface ExtractedFields {
  businessName: string | null;
  businessAddress: string | null;
  phoneNumber: string | null;
  aboutBusiness: string | null;
  uniqueSellingPoints: string | null;
  credentials: string | null;
  voiceTones: string[] | null;
  pointOfView: string | null;
  writingVoiceProfile: string | null;
  wordsToAlwaysUse: string | null;
  ctaText: string | null;
  socialProfiles: string | null;
  businessLogoUrl: string | null;
  averageRating: number | null;
  totalReviewCount: number | null;
  // GBP fields (Primary Category, Secondary Categories, Services) are NOT
  // extracted by Auto-Fill. Use the Paste-GBP textarea (parseGBP) instead —
  // that uses the actual GBP dashboard as the source of truth.
}

export async function extractFromUrl(websiteUrl: string): Promise<{
  extracted: ExtractedFields;
  costCents: number;
  pageTitle: string;
  pageFinalUrl: string;
}> {
  return request("/api/ai/extract-from-url", {
    method: "POST",
    body: JSON.stringify({ websiteUrl }),
  });
}

// ===== Master Auto-Fill (chunk 3y) =====
export interface MasterAutofillResult {
  data: ExtractedFields;
  websiteExtraction: {
    status: "ok" | "failed";
    message: string;
  };
  costCents: number;
}

export async function masterAutofill(
  clientId: number
): Promise<MasterAutofillResult> {
  return request(`/api/clients/${clientId}/autofill-master`, {
    method: "POST",
  });
}

export interface ParsedGBP {
  primaryCategory: string | null;
  secondaryCategories: string[];
  services: Record<string, string[]>;
}

export async function parseGBP(text: string): Promise<{
  parsed: ParsedGBP;
  costCents: number;
}> {
  return request("/api/ai/parse-gbp", {
    method: "POST",
    body: JSON.stringify({ text }),
  });
}

// ===== Articles & Content Generation =====
export type ContentType = "service_page" | "category_page" | "location_page" | "geo_page" | "supporting_content" | "attractions" | "dental_education";
export type Mode = "core30" | "optimized";
export type ArticleStatus = "draft" | "queued" | "generating" | "completed" | "failed";
export type OutputFormat = "docx" | "html" | "plain_text";

export interface ResearchData {
  paaQuestions: string[];
  relatedKeywords: string[];
  competitorUrls: string[];
  competitorCount: number;
  source: "dataforseo" | "stub";
  message: string;
}

/** chunk3z-fix366: AI-detection result for one detector on one version. */
export interface AiLeg {
  score: number | null;
  flagged: string[];
  source: string;
}
export interface AiVersionScores {
  zerogpt: AiLeg | null;
  winston: AiLeg | null;
}

/** chunk3z-fix369: one proofreading error span. */
export interface ErrorSpan {
  text: string;
  type: "spelling" | "grammar" | "other";
}

export interface Article {
  id: number;
  clientId: number;
  title: string;
  contentType: ContentType;
  mode: Mode;
  status: ArticleStatus;
  outlineText: string | null;
  outlineApproved: boolean;
  /** fix636: persisted Full Auto intent — draft this article automatically once
   *  its outline lands (approved). Lets the auto-draft watcher rehydrate after
   *  navigation instead of relying on a lost in-memory ref. */
  autoDraftAfterOutline?: boolean;
  researchBriefText: string | null;
  researchData: ResearchData | null;
  // chunk3z-fix143: per-article override rules (QueueRow "Rules" popup /
  // OutlineEditor "Add rules & rewrite"). null when none set.
  articleOverrides: string | null;
  // Background outline-queue state (chunk3z-fix76): null | 'queued' |
  // 'researching' | 'outlining' | 'done' | 'failed'. Survives navigation.
  outlineStatus: string | null;
  outlineError?: string | null;
  /** fix583: authoritative page title "{Service} {City}, {ST}" from the backend. */
  pageTitle?: string | null;
  /** fix584: pre-generated styled HTML (styled during generation). Lets the
   *  preview's "Styled" toggle render instantly instead of re-styling. */
  styledHtml?: string | null;
  /** fix580: true when the outline changed (unapproved/regenerated) after the
   *  article was written — the existing draft is kept but flagged out-of-date. */
  outlineStale?: boolean;
  targetService: string | null;
  targetCity: string | null;
  targetLandmark: string | null;
  targetDistanceLabel: string | null;
  outputFormat: OutputFormat;
  imagesPerPage: number;
  imagesGenerated: number;
  imagesFailed: number;
  aiScore: number | null;
  articleMarkdown: string | null;
  /** chunk3z-fix366: pre-humanization draft (raw AI article) for the
   *  [AI | HUMANIZED] reader toggle. Null for pre-fix366 articles or when
   *  humanize was skipped. */
  originalMarkdown?: string | null;
  /** chunk3z-fix366: per-version, per-detector AI scores + flagged sentences.
   *  { original|final: { zerogpt|winston: { score, flagged[], source } } } */
  aiVersions?: {
    original: AiVersionScores | null;
    final: AiVersionScores;
  } | null;
  /** chunk3z-fix369: proofreading error spans per version for [ERRORS] toggle. */
  errorSpans?: {
    original: ErrorSpan[] | null;
    final: ErrorSpan[];
  } | null;
  /** fix302: W2 - Attractions — array of per-attraction articles, else null. */
  attractionsData?:
    | Array<{
        name: string;
        address?: string;
        website?: string;
        markdown: string;
        wordCount?: number;
      }>
    | null;
  // fix309: Dental Education plan { topics, approvedCategories, approveAll }.
  educationData?: {
    topics: Array<{
      id: string; category: string; topic: string; tags: string[];
      tier: number; offered: boolean; linkTarget: string;
      status: string; markdown?: string; wordCount?: number; aiScore?: number | null;
    }>;
    approvedCategories?: string[];
    approveAll?: boolean;
  } | null;
  wordCount: number | null;
  generationCostCents: number | null;
  generationDurationMs: number | null;
  createdAt: string;
  updatedAt: string;
  completedAt: string | null;
  currentStep: string | null;
}

export interface PassResult {
  passName: string;
  durationMs: number;
  costCents: number;
  ok: boolean;
  message?: string;
}

export interface JobResult {
  passes?: PassResult[];
  aiScore?: number | null;
  wordCount?: number;
  totalCostCents?: number;
}

export interface Job {
  id: number;
  articleId: number;
  status: string;
  currentStep: string | null;
  errorMessage: string | null;
  result: JobResult | null;
  startedAt: string | null;
  completedAt: string | null;
}

export async function listArticles(clientId: number): Promise<Article[]> {
  const data = await request<{ articles: Article[] }>(`/api/clients/${clientId}/articles`);
  return data.articles;
}

export async function getArticle(id: number): Promise<{ article: Article; latestJob: Job | null }> {
  return request(`/api/articles/${id}`);
}

// fix251: on-demand AI-detection check for a specific provider.
export interface AiCheckResult {
  provider: "zerogpt" | "winston";
  aiScore: number;
  source: string;
  wordsScored: number | null;
  creditsRemaining: number | null;
  message: string;
}
export async function checkArticleAiScore(
  id: number,
  provider: "zerogpt" | "winston"
): Promise<AiCheckResult> {
  return request(`/api/articles/${id}/ai-check`, {
    method: "POST",
    body: JSON.stringify({ provider }),
  });
}

// chunk3z-fix373: floating "+ Edit" bar — apply a plain-English instruction to
// the current version. Returns either an edited markdown (not saved) or a
// clarifying question.
export interface EditResult {
  status: "clarify" | "edited" | "noop";
  question?: string;
  options?: string[];
  newMarkdown?: string;
  summary?: string;
  costCents: number;
}
export async function editArticleSection(
  id: number,
  payload: {
    instruction: string;
    version: "ai" | "humanized";
    clarification?: string;
  }
): Promise<EditResult> {
  return request(`/api/articles/${id}/edit`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

// chunk3z-fix373: after saving an edit, re-run AI scoring + proofreading on the
// current text so the reader toggles light back up.
export async function recheckArticle(
  id: number,
  version: "ai" | "humanized"
): Promise<{ ok: boolean; aiVersions: unknown; errorSpans: unknown }> {
  return request(`/api/articles/${id}/recheck`, {
    method: "POST",
    body: JSON.stringify({ version }),
  });
}

export async function createArticle(
  clientId: number,
  payload: {
    title: string;
    contentType: ContentType;
    mode: Mode;
    targetService?: string;
    targetCity?: string;
    outline?: string;
    researchBrief?: string;
    outputFormat?: OutputFormat;
    imagesPerPage?: number;
  }
): Promise<{ id: number }> {
  return request(`/api/clients/${clientId}/articles`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function updateArticle(
  id: number,
  payload: {
    outline?: string;
    researchBrief?: string;
    articleMarkdown?: string;
    /** chunk3z-fix373: save an edit made to the AI (original) version. */
    originalMarkdown?: string;
    mode?: Mode;
    title?: string;
    outlineApproved?: boolean;
    outputFormat?: OutputFormat;
    imagesPerPage?: number;
    contentType?: ContentType;
    targetService?: string;
    targetCity?: string;
    targetLandmark?: string;
    articleOverrides?: string;
    attractionsData?: Array<{
      name: string;
      address?: string;
      website?: string;
      markdown: string;
      wordCount?: number;
      aiScore?: number | null;
    }>;
  }
): Promise<{ ok: true }> {
  return request(`/api/articles/${id}`, {
    method: "PATCH",
    body: JSON.stringify(payload),
  });
}

export async function deleteArticle(id: number): Promise<void> {
  await request(`/api/articles/${id}`, { method: "DELETE" });
}

export interface PipelineOptions {
  mode: Mode;
  useEightPassEngine: boolean;
  humanize: boolean;
  aiDetection: boolean;
  outboundLinks: boolean;
  writingModel?: string;
}

export async function generateArticleApi(
  id: number,
  pipelineOptions: PipelineOptions
): Promise<{ ok: true; articleId: number }> {
  return request(`/api/articles/${id}/generate`, {
    method: "POST",
    body: JSON.stringify({ pipelineOptions }),
  });
}

export async function cancelArticleGeneration(
  id: number
): Promise<{ ok: boolean; liveCancel?: boolean; clearedStuck?: boolean }> {
  return request(`/api/articles/${id}/cancel`, { method: "POST" });
}

export function getDocxDownloadUrl(id: number): string {
  return `/api/articles/${id}/export.docx`;
}

// Download formats offered in the Review & Export download dropdown.
// Distinct from OutputFormat (the article's stored default) — these are
// on-demand export targets.
export type DownloadFormat = "docx" | "html" | "txt" | "txt_html" | "md";

export function getDownloadUrl(id: number, format: OutputFormat): string {
  if (format === "html") return `/api/articles/${id}/export.html`;
  if (format === "plain_text") return `/api/articles/${id}/export.txt`;
  return `/api/articles/${id}/export.docx`;
}

// Build a download URL for any of the 5 dropdown formats.
export function getDownloadUrlFor(
  id: number,
  format: DownloadFormat,
  version: "ai" | "humanized" = "humanized"
): string {
  // chunk3z-fix371: ?version=ai downloads the original pre-humanization draft;
  // default (humanized) downloads the final article. Backend falls back to the
  // final article when no AI original is stored (older articles).
  const q = version === "ai" ? "?version=ai" : "";
  switch (format) {
    case "html":
      return `/api/articles/${id}/export.html${q}`;
    case "txt":
      return `/api/articles/${id}/export.txt${q}`;
    case "txt_html":
      return `/api/articles/${id}/export.html.txt${q}`;
    case "md":
      return `/api/articles/${id}/export.md${q}`;
    case "docx":
    default:
      return `/api/articles/${id}/export.docx${q}`;
  }
}

// Build the "download all as zip" URL for a client + format. The backend
// bundles every completed article into articles-<format>.zip.
export function getDownloadAllUrl(clientId: number, format: DownloadFormat): string {
  const extMap: Record<DownloadFormat, string> = {
    docx: "docx",
    html: "html",
    txt: "txt",
    txt_html: "htmltxt",
    md: "md",
  };
  return `/api/clients/${clientId}/articles/download-all.${extMap[format]}`;
}

// ===== Article images (chunk 3n) =====
export interface ArticleImageMeta {
  id: number;
  position: number;
  altText: string | null;
  prompt: string | null;
  mimeType: string;
  status: "completed" | "failed";
  errorMessage: string | null;
  imageData: string | null;
}

export interface RegenerateImagesResult {
  ok: boolean;
  summary: {
    attempted: number;
    succeeded: number;
    failed: number;
    costCents: number;
    message: string;
  };
}

export async function listArticleImages(
  articleId: number,
  includeData = false
): Promise<{ images: ArticleImageMeta[] }> {
  const qs = includeData ? "?includeData=1" : "";
  return request(`/api/articles/${articleId}/images${qs}`);
}

export async function regenerateArticleImages(
  articleId: number
): Promise<RegenerateImagesResult> {
  return request(`/api/articles/${articleId}/regenerate-images`, {
    method: "POST",
  });
}

// ===== GBP Category Audit =====
export interface CategoryUsageRow {
  category: string;
  count: number;
  percent: number;
}

export interface YourCategoryRow {
  name: string;
  isPrimary: boolean;
  alsoUsedByCompetitors: boolean;
}

export interface MissingCategoryRow {
  name: string;
  competitorCount: number;
}

export interface CompetitorReviewSnapshot {
  title: string;
  rank: number;
  rating: number | null;
  reviewCount: number | null;
}

export interface ReviewsComparison {
  sampleSize: number;
  medianRating: number | null;
  medianReviewCount: number | null;
  topRanked: CompetitorReviewSnapshot[];
}

export interface YourListingFromGoogle {
  matchedTitle: string;
  rank: number;
  matchConfidence: number;
  rating: number | null;
  reviewCount: number | null;
}

export interface GbpCategoryAuditResult {
  stubbed: boolean;
  competitorCount: number;
  categoryUsage: CategoryUsageRow[];
  yourCategories: YourCategoryRow[];
  missingCategories: MissingCategoryRow[];
  /** Reviews data is null when stubbed or when no rating data was available. */
  reviewsComparison: ReviewsComparison | null;
  /** Client's own listing match from Maps API. Backend auto-writes the
   *  rating + review count to the client record when this is non-null. */
  yourListing: YourListingFromGoogle | null;
  ranAt: string;
  /** Human-readable provenance string. Set by backend on every run. */
  message?: string;
}

export async function getGbpCategoryAudit(
  clientId: number
): Promise<{ result: GbpCategoryAuditResult | null; ranAt: string | null }> {
  return request(`/api/clients/${clientId}/gbp-category-audit`);
}

export async function runGbpCategoryAudit(
  clientId: number,
  payload: { primaryCategory: string; city: string }
): Promise<{ result: GbpCategoryAuditResult }> {
  return request(`/api/clients/${clientId}/gbp-category-audit`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

// ===== Site Crawler =====
export interface CrawlPage {
  id: number;
  url: string;
  path: string;
  title: string | null;
  h1: string | null;
  metaDescription: string | null;
  wordCount: number | null;
  internalLinkCount: number | null;
  externalLinkCount: number | null;
  topH2s: string[] | null;
  topH3s: string[] | null;
  topH4s: string[] | null;
  topH5s: string[] | null;
  topH6s: string[] | null;
  hasH1: boolean;
  autoAssignment: string | null;
  /** Status of the auto-classification — drives badge rendering on the
   *  Site Crawler tab. See backend AutoStatus type for full meaning. */
  autoStatus: "auto" | "check" | "missing" | "none" | "homepage";
  manualAssignment: string | null;
  source: string;
  updatedAt: string;
}

export interface CrawlStats {
  totalPages: number;
  avgWords: number;
  noH1Count: number;
  totalInternalLinks: number;
  totalExternalLinks: number;
}

export interface CrawlCategoryUniverse {
  primary: string | null;
  secondaries: string[];
  services: string[];
}

export interface CrawlBundle {
  websiteUrl: string;
  lastCrawledAt: string | null;
  lastCrawlSource: string | null;
  universe: CrawlCategoryUniverse;
  stats: CrawlStats;
  pages: CrawlPage[];
}

export async function getCrawl(clientId: number): Promise<CrawlBundle> {
  return request(`/api/clients/${clientId}/crawl`);
}

export async function runCrawl(
  clientId: number,
  payload: { websiteUrl?: string; mode?: "full" | "new" }
): Promise<{
  ok: true;
  stubbed: boolean;
  pageCount: number;
  mode: "full" | "new";
}> {
  return request(`/api/clients/${clientId}/crawl`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function uploadCrawlCsv(
  clientId: number,
  csv: string
): Promise<{ ok: true; pageCount: number }> {
  return request(`/api/clients/${clientId}/crawl/upload-csv`, {
    method: "POST",
    body: JSON.stringify({ csv }),
  });
}

export async function saveCrawlAssignments(
  clientId: number,
  assignments: { pageId: number; manualAssignment: string | null }[]
): Promise<{ ok: true; count: number }> {
  return request(`/api/clients/${clientId}/crawl/save-assignments`, {
    method: "POST",
    body: JSON.stringify({ assignments }),
  });
}

// ===== Research =====
export interface ContentGapMatch {
  service: string;
  /** Whether this is a GBP category (primary or secondary) or a GBP
   *  service. Drives the badge/icon shown next to the row in the UI. */
  kind: "category" | "service";
  page: {
    id: number;
    path: string;
    url: string;
    title: string | null;
    wordCount: number | null;
  };
  /** chunk3z-fix107: present when this service is covered as a variant nested on
   *  a shared parent page (e.g. "Porcelain Veneers" on the /dental-veneers/
   *  page) rather than by its own dedicated page. */
  variantOnPath?: string;
}

/** Missing-pages entry — same `kind` distinction as ContentGapMatch. */
export interface ContentGapMissing {
  name: string;
  kind: "category" | "service";
  /** chunk3z-fix103: if this missing service is currently a blurb/section on a
   *  non-dedicated page, where it lives. */
  blurbOnPath?: string;
  blurbOnUrl?: string;
  /** chunk3z-fix561: title of the page the service was found on. */
  blurbOnTitle?: string | null;
}

export interface ContentGapResult {
  hasCrawl: boolean;
  total: number;
  covered: number;
  coveragePercent: number;
  matches: ContentGapMatch[];
  missing: ContentGapMissing[];
  /** chunk3z-fix102: service-looking site pages not tied to any GBP service. */
  unmatchedPages?: {
    id: number;
    path: string;
    url: string;
    title: string | null;
  }[];
  /** chunk3z-fix562: content-first scan — headings found on /services/ hub
   *  pages that look like services but have no dedicated page. `inGbp` flags
   *  the ones that are also a GBP gap. */
  foundServices?: {
    service: string;
    onPath: string;
    onUrl: string;
    onTitle: string | null;
    inGbp: boolean;
  }[];
  /** chunk3z-fix567: lowercased names of services/categories that already have
   *  an article in the writing queue. Used to show a persistent "in queue"
   *  state on both gap sections. */
  existingQueueServices?: string[];
}

export type OverlapVerdict =
  | "very_distinct"
  | "distinct"
  | "moderate"
  | "high_overlap";

export interface CompetitorListing {
  name: string;
  rating: number | null;
  reviewCount: number | null;
  url: string;
}

export interface EntityOverlapRow {
  entity: string;
  resultsCount: number;
  overlapPercent: number;
  verdict: OverlapVerdict;
  topBusinesses: CompetitorListing[];
  overlappingNames: string[];
}

export interface EntityOverlapResult {
  stubbed: boolean;
  seedKeyword: string;
  city: string;
  rows: EntityOverlapRow[];
  ranAt: string;
}

export async function getContentGap(clientId: number): Promise<ContentGapResult> {
  return request(`/api/clients/${clientId}/research/content-gap`);
}

// ===== Content Audit =====
export interface ContentAuditRow {
  id: number;
  url: string;
  path: string;
  title: string | null;
  currentAiScore: number | null;
  previousAiScore: number | null;
  bestAiScore: number | null;
  worstAiScore: number | null;
  scoreSource: string | null;
  scoredAt: string | null;
  previousScoredAt: string | null;
  isUpdated: boolean;
  isNew: boolean;
  lastError: string | null;
  wordCount: number | null;
}

export interface ContentAuditResult {
  hasCrawl: boolean;
  rows: ContentAuditRow[];
}

export async function getContentAudit(
  clientId: number
): Promise<ContentAuditResult> {
  return request(`/api/clients/${clientId}/content-audit`);
}

export interface DetectionProvider {
  id: "winston" | "zerogpt" | "gptzero" | "heuristic";
  label: string;
  status: "ready" | "no_key" | "always";
  note?: string;
}

export async function getDetectionProviders(): Promise<{
  providers: DetectionProvider[];
}> {
  return request(`/api/content-audit/providers`);
}

export async function scanContentAudit(
  clientId: number,
  opts?: { pageIds?: number[]; provider?: string }
): Promise<{
  ok: true;
  scored: number;
  failed: number;
  /** chunk3z-fix379: pages that silently used the built-in estimate. */
  fellBack?: number;
  fallbackReason?: string | null;
  rows: ContentAuditRow[];
}> {
  return request(`/api/clients/${clientId}/content-audit/scan`, {
    method: "POST",
    body: JSON.stringify({
      ...(opts?.pageIds ? { pageIds: opts.pageIds } : {}),
      ...(opts?.provider ? { provider: opts.provider } : {}),
    }),
  });
}

export async function patchContentAudit(
  clientId: number,
  auditId: number,
  fields: { isUpdated?: boolean; isNew?: boolean }
): Promise<{ ok: true }> {
  return request(`/api/clients/${clientId}/content-audit/${auditId}`, {
    method: "PATCH",
    body: JSON.stringify(fields),
  });
}

export async function getEntityOverlap(
  clientId: number
): Promise<{
  result: EntityOverlapResult | null;
  ranAt: string | null;
  seedKeyword: string | null;
  city: string | null;
}> {
  return request(`/api/clients/${clientId}/research/entity-overlap`);
}

export async function runEntityOverlap(
  clientId: number,
  payload: { seedKeyword: string; city: string; candidateEntities: string[] }
): Promise<{ result: EntityOverlapResult }> {
  return request(`/api/clients/${clientId}/research/entity-overlap`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function importMissingToBuildQueue(
  clientId: number,
  payload: {
    services?: string[];
    contentType?: string;
    items?: { name: string; kind?: string }[];
  }
): Promise<{ ok: true; created: { id: number; service: string }[] }> {
  return request(
    `/api/clients/${clientId}/research/import-to-content-generation`,
    {
      method: "POST",
      body: JSON.stringify(payload),
    }
  );
}

// ===== Supporting Content Planner =====
export type SuppPlannerSource = "paa" | "related" | "reddit" | "ai";

export interface SuppPlannerScoredItem {
  rawQuery: string;
  source: SuppPlannerSource;
  sourceUrl?: string;
  title: string;
  score: number;
  reasoning: string;
}

/** Per-source breakdown of fetched candidate counts (BEFORE scoring drops any).
 *  Used by the diagnostic strip in the Supp Planner UI so users can see at a
 *  glance which source delivered or didn't. */
export interface SuppPlannerSourceCounts {
  paa: number;
  related: number;
  reddit: number;
  ai: number;
  total: number;
}

export interface SuppPlannerResult {
  serviceTerm: string;
  city: string;
  candidateCount: number;
  /** Per-source counts. Optional for backward-compat with older cached runs
   *  that don't have this field — frontend should fall back gracefully. */
  sourceCounts?: SuppPlannerSourceCounts;
  items: SuppPlannerScoredItem[];
  costCents: number;
  ranAt: string;
}

export interface SuppPlannerPastRun {
  serviceTerm: string;
  candidateCount: number;
  updatedAt: string;
}

export async function listSuppPlannerRuns(
  clientId: number
): Promise<{ runs: SuppPlannerPastRun[] }> {
  return request(`/api/clients/${clientId}/supp-planner`);
}

export async function getSuppPlannerRun(
  clientId: number,
  serviceTerm: string
): Promise<{ result: SuppPlannerResult | null; updatedAt?: string }> {
  return request(
    `/api/clients/${clientId}/supp-planner/${encodeURIComponent(serviceTerm)}`
  );
}

export async function runSuppPlannerResearch(
  clientId: number,
  serviceTerm: string
): Promise<{ result: SuppPlannerResult }> {
  return request(`/api/clients/${clientId}/supp-planner/research`, {
    method: "POST",
    body: JSON.stringify({ serviceTerm }),
  });
}

export async function importSuppPlannerToBuildQueue(
  clientId: number,
  items: { title: string; rawQuery?: string; source?: string }[]
): Promise<{ ok: true; created: { id: number; title: string }[] }> {
  return request(
    `/api/clients/${clientId}/supp-planner/import-to-content-generation`,
    {
      method: "POST",
      body: JSON.stringify({ items }),
    }
  );
}

// ===== Geo Relevance Planner =====
export interface GeoNeighborhoodAnalysis {
  name: string;
  score: number;
  avgRank: number;
  distanceLabel: string;
  bearing: string;
  narrative: string;
  improvement: "High" | "Medium" | "Low";
  easeOfRanking: number;
  trafficPotential: number;
  contentTopics: string[];
  /** fix690: real Places-verified landmarks for this area (ground truth for the writer). */
  landmarks: {
    name: string;
    type: string;
    fallsInNeighborhood?: string;
    distanceLabel?: string;
  }[];
  /** fix668: "city" | "neighborhood" — lets the analysis header split the count. */
  areaType: "city" | "neighborhood";
}

export interface GeoTargetServiceRow {
  priority: number;
  neighborhoodName: string;
  targetService: string;
  targetLandmark: string;
  avgRank: number;
  distanceLabel: string;
  /** fix661: "city" | "neighborhood" — drives the near/in title + a UI badge. */
  areaType?: "city" | "neighborhood";
}

export interface GeoPlannerResult {
  targetService: string;
  city: string;
  gridPoints: number;
  competitorCount: number;
  neighborhoods: GeoNeighborhoodAnalysis[];
  targetServices: GeoTargetServiceRow[];
  costCents: number;
  ranAt: string;
  /** fix662: "places" = real Google scan; "stub" = placeholder data. */
  dataSource?: "places" | "stub";
  fallbackReason?: string;
}

export interface GeoPlannerPastRun {
  targetService: string;
  city: string;
  neighborhoodCount: number;
  targetServiceCount: number;
  updatedAt: string;
}

export async function listGeoPlannerRuns(
  clientId: number
): Promise<{ runs: GeoPlannerPastRun[] }> {
  return request(`/api/clients/${clientId}/geo-planner`);
}

export async function getGeoPlannerRun(
  clientId: number,
  targetService: string
): Promise<{ result: GeoPlannerResult | null; updatedAt?: string }> {
  return request(
    `/api/clients/${clientId}/geo-planner/${encodeURIComponent(targetService)}`
  );
}

export async function runGeoPlannerResearch(
  clientId: number,
  payload: { targetService: string; city: string }
): Promise<{ result: GeoPlannerResult }> {
  return request(`/api/clients/${clientId}/geo-planner/research`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function importGeoPlannerToBuildQueue(
  clientId: number,
  items: {
    targetService: string;
    targetLandmark?: string;
    neighborhoodName?: string;
    distanceLabel?: string;
  }[]
): Promise<{ ok: true; created: { id: number; title: string }[] }> {
  return request(
    `/api/clients/${clientId}/geo-planner/import-to-content-generation`,
    {
      method: "POST",
      body: JSON.stringify({ items }),
    }
  );
}

// ===== Brief + Outline auto-generation =====
export interface BriefOutlineResult {
  ok: true;
  brief: string;
  outline: string;
  outlineApproved: boolean;
  costCents: number;
  durationMs: number;
}

export async function generateBriefAndOutline(
  articleId: number,
  autoApprove: boolean
): Promise<BriefOutlineResult> {
  // chunk3z-fix47: bumped from 240s (4 min) → 660s (11 min) to match backend's
  // new per-stage 300s timeouts (5 min × 2 stages = 10 min worst case + 1 min
  // buffer). Opus 4.7 doing a complex 8000-token brief routinely needs 3-4 min;
  // the previous 3-min backend timeout was firing before the call finished.
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 660_000);
  try {
    return await request(`/api/articles/${articleId}/generate-brief-and-outline`, {
      method: "POST",
      body: JSON.stringify({ autoApprove }),
      signal: controller.signal,
    });
  } catch (err) {
    // The fetch wraps abort errors as ApiError("Network error..."). Detect that
    // case via the underlying signal and rethrow as a clearer message so the
    // user sees something useful in the alert dialog.
    if (controller.signal.aborted) {
      throw new ApiError(
        "Outline generation timed out after 11 minutes. The server may be overloaded — try again in a moment, or use the recovery button to clear stuck rows.",
        0
      );
    }
    throw err;
  } finally {
    clearTimeout(timeoutId);
  }
}

export async function approveOutline(articleId: number): Promise<{ ok: true }> {
  return request(`/api/articles/${articleId}/approve-outline`, {
    method: "POST",
  });
}

/**
 * chunk3z-fix147: regenerate (rewrite + humanize) ONE section of an article.
 * Returns the new section body; caller splices it in and saves explicitly.
 */
export async function regenerateArticleSection(
  articleId: number,
  sectionHeading: string,
  sectionBody: string
): Promise<{ ok: true; newBody: string; humanized: boolean; costCents: number }> {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 200_000);
  try {
    return await request(`/api/articles/${articleId}/regenerate-section`, {
      method: "POST",
      body: JSON.stringify({ sectionHeading, sectionBody }),
      signal: controller.signal,
    });
  } finally {
    clearTimeout(timeoutId);
  }
}

/**
 * Background outline queue (chunk3z-fix76). Enqueues articles for server-side,
 * ONE-AT-A-TIME outline generation (research → brief → outline per article).
 * Returns immediately; processing continues even if the user navigates away.
 * Poll article.outlineStatus to track progress.
 */
export async function enqueueOutlines(
  clientId: number,
  articleIds: number[],
  autoApprove: boolean,
  autoDraft = false,
  autoDraftOptions?: PipelineOptions
): Promise<{ ok: true; enqueued: number[] }> {
  return request(`/api/clients/${clientId}/outline-queue/enqueue`, {
    method: "POST",
    body: JSON.stringify({ articleIds, autoApprove, autoDraft, autoDraftOptions }),
  });
}

export interface PromptStage {
  stage: string;
  label: string;
  template: string | null;
  editable: boolean;
}
export interface PromptPageType {
  pageType: string;
  label: string;
  stages: PromptStage[];
}
export interface PromptMode {
  mode: string;
  label: string;
  locked: boolean;
  writing: PromptStage;
  pageTypes: PromptPageType[];
}

export async function getPromptCatalog(): Promise<{ ok: true; modes: PromptMode[] }> {
  return request(`/api/prompts/catalog`);
}

export async function saveSmcCustomPrompt(
  pageType: string,
  stage: string,
  template: string
): Promise<{ ok: true; pageType: string; stage: string; template: string }> {
  return request(`/api/prompts/smc-custom`, {
    method: "PUT",
    body: JSON.stringify({ pageType, stage, template }),
  });
}

export async function resumeOutlineQueue(
  clientId: number,
  articleIds: number[]
): Promise<{ ok: true; enqueued: number[] }> {
  return request(`/api/clients/${clientId}/outline-queue/resume`, {
    method: "POST",
    body: JSON.stringify({ articleIds }),
  });
}

export async function cancelOutlineQueueJob(
  articleId: number
): Promise<{ ok: true; cancelled: boolean }> {
  return request(`/api/articles/${articleId}/outline-queue/cancel`, {
    method: "POST",
  });
}

export async function cancelAllOutlineQueueJobs(): Promise<{
  ok: true;
  cancelledCount: number;
}> {
  return request(`/api/outline-queue/cancel-all`, { method: "POST" });
}

/**
 * chunk3z-fix32: Pass 0 — gather research data (PAA, related, competitors).
 * Returns within ~5s so the frontend can render the RESEARCH DATA block
 * BEFORE brief/outline generation finishes. Idempotent: returns cached
 * `research_data` for the article if one exists, unless `force=true`.
 *
 * Typical flow: frontend fires this BEFORE generateBriefAndOutline so the
 * user sees research data appear within seconds, then brief+outline pop in
 * 1-2 minutes later.
 */
export interface GatherResearchResult {
  ok: true;
  researchData: ResearchData;
  fromCache: boolean;
}

export async function gatherResearch(
  articleId: number,
  force = false
): Promise<GatherResearchResult> {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 60_000);
  try {
    return await request(`/api/articles/${articleId}/gather-research`, {
      method: "POST",
      body: JSON.stringify({ force }),
      signal: controller.signal,
    });
  } catch (err) {
    if (controller.signal.aborted) {
      throw new ApiError(
        "Research gathering timed out after 60 seconds. DataForSEO may be slow — try again, or check your API key in Settings.",
        0
      );
    }
    throw err;
  } finally {
    clearTimeout(timeoutId);
  }
}

// ===== System Settings =====
export type SettingKey =
  | "anthropic_api_key"
  | "openai_api_key"
  | "dataforseo_login"
  | "dataforseo_password"
  | "pictory_client_id"
  | "pictory_client_secret"
  | "winston_ai_api_key"
  | "google_gemini_api_key"
  | "stealthgpt_api_key"
  | "google_places_api_key"
  | "google_oauth_client_id"
  | "gsc_client_id"
  | "gsc_client_secret"
  | "gsc_redirect_uri"
  | "google_oauth_client_secret"
  | "zerogpt_api_key"
  | "gptzero_api_key"
  | "image_generation_provider"
  | "ai_detection_provider"
  | "humanize_model"
  | "writing_model"
  | "refinement_mode";

export interface SettingStatus {
  key: SettingKey;
  isSet: boolean;
  source: "ui" | "env" | "none";
  validationStatus: "valid" | "low_credits" | "invalid" | null;
  validationMessage: string | null;
  validatedAt: string | null;
  value: string | null;
}

export async function getSettings(): Promise<{ settings: SettingStatus[] }> {
  return request("/api/settings");
}

export async function setSetting(
  key: SettingKey,
  value: string | null
): Promise<{ ok: true }> {
  return request(`/api/settings/${key}`, {
    method: "PATCH",
    body: JSON.stringify({ value }),
  });
}

export async function testSetting(
  key: SettingKey
): Promise<{ status: "valid" | "low_credits" | "invalid"; message: string }> {
  return request(`/api/settings/${key}/test`, { method: "POST" });
}

// ===== Content Runs (Run History) =====

export interface ContentRun {
  id: number;
  client_id: number;
  started_at: string;
  finished_at: string | null;
  status: "running" | "completed" | "failed" | "cancelled";
  total_articles: number;
  completed_articles: number;
  failed_articles: number;
  article_ids: number[];
  /** fix215: 'article' (draft generation) | 'outline' (brief+outline run). */
  run_type?: "article" | "outline";
}

export async function listContentRuns(clientId: number): Promise<ContentRun[]> {
  const data = await request<{ runs: ContentRun[] }>(
    `/api/clients/${clientId}/content-runs`
  );
  return data.runs;
}

export async function createContentRun(
  clientId: number,
  articleIds: number[]
): Promise<ContentRun> {
  const data = await request<{ run: ContentRun }>(
    `/api/clients/${clientId}/content-runs`,
    {
      method: "POST",
      body: JSON.stringify({ articleIds }),
    }
  );
  return data.run;
}

export async function cancelContentRun(
  clientId: number,
  runId: number
): Promise<void> {
  await request(`/api/clients/${clientId}/content-runs/${runId}/cancel`, {
    method: "POST",
  });
}

// ===========================================================================
// chunk3z-fix387: Site migration (Sitemaps tab)
// ===========================================================================

export interface MigrationRecord {
  id: number;
  clientId: number;
  domain: string;
  launchDate: string | null;
  /** chunk3z-fix388: Gen2/Gen3 boundary — when the PREVIOUS site relaunched. */
  prevRelaunchDate: string | null;
  /** chunk3z-fix504: persisted "not launched yet" intent. */
  notLaunched?: boolean;
  /** fix620: persisted resolved sitemap URL (Yoast/Wix/etc.). */
  sitemapUrl?: string | null;
  status: string;
  lastError: string | null;
}

export interface MigrationUrl {
  id: number;
  url: string;
  path: string;
  firstCapture: string | null;
  lastCapture: string | null;
  generation: "gen1" | "gen2" | "gen3" | null;
  source: string;
  isJunk: boolean;
  junkReason: string | null;
  gscImpressions: number | null;
  gscClicks: number | null;
}

export interface MigrationTarget {
  id: number;
  url: string;
  path: string;
  sitemapType: string | null;
  title: string | null;
}

export interface MigrationCounts {
  total: number;
  kept: number;
  junk: number;
  targets: number;
  gen1: number;
  gen2: number;
  gen3: number;
  /** chunk3z-fix434: provenance breakdown of kept URLs. */
  srcWayback: number;
  srcGsc: number;
  srcBoth: number;
}

export async function getMigration(clientId: number): Promise<{
  migration: MigrationRecord | null;
  urls: MigrationUrl[];
  targets: MigrationTarget[];
  // chunk3z-fix510: path → 'page' | 'blog' | 'service' manual category overrides.
  overrides?: Record<string, string>;
  counts: MigrationCounts | null;
}> {
  return request(`/api/clients/${clientId}/migration`);
}

// chunk3z-fix510: persist a manual page/blog/service move so it survives a
// rescan. Pass category null to clear (revert to the auto heuristic).
export async function setMigrationUrlOverride(
  clientId: number,
  path: string,
  category: "page" | "blog" | "service" | null
): Promise<{ ok: boolean }> {
  return request(`/api/clients/${clientId}/migration/override`, {
    method: "PATCH",
    body: JSON.stringify({ path, category }),
  });
}

export async function runWaybackScan(
  clientId: number,
  payload: {
    domain?: string;
    launchDate?: string | null;
    notLaunched?: boolean;
    /** Cutoff year — URLs last captured before this are marked junk. */
    minYear?: number;
    prevRelaunch?: string;
  }
): Promise<{
  ok: boolean;
  total: number;
  kept: number;
  junk: number;
  prevRelaunch: string | null;
}> {
  return request(`/api/clients/${clientId}/migration/scan`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function importSitemap(
  clientId: number,
  payload: {
    xml?: string;
    url?: string;
    replace?: boolean;
    launchDate?: string | null;
  }
): Promise<{ ok: boolean; imported: number; total: number; imageFiltered?: number; resolvedSitemapUrl?: string | null; locationScopedOut?: number; locationPrefix?: string | null }> {
  return request(`/api/clients/${clientId}/migration/sitemap`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

// fix602: Client Style Examples + Style Profile (Build Queue).
export interface StyleExampleMeta {
  id: number;
  filename: string;
  mimeType: string;
  source: string;
  sourceUrl: string | null;
  uploadedAt: string;
}
export async function listStyleExamples(
  clientId: number
): Promise<{
  examples: StyleExampleMeta[];
  styleProfile: string | null;
  styleProfileAt: string | null;
}> {
  return request(`/api/clients/${clientId}/style-examples`);
}
export async function getStyleExample(
  clientId: number,
  exampleId: number
): Promise<{
  id: number;
  filename: string;
  mimeType: string;
  dataUrl: string | null;
  sourceUrl?: string | null;
  htmlSnippet?: string | null;
}> {
  return request(`/api/clients/${clientId}/style-examples/${exampleId}`);
}
export async function uploadStyleExample(
  clientId: number,
  payload: { filename: string; mimeType: string; data: string }
): Promise<{ ok: true; id: number }> {
  return request(`/api/clients/${clientId}/style-examples`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}
export async function deleteStyleExample(
  clientId: number,
  exampleId: number
): Promise<void> {
  await request(`/api/clients/${clientId}/style-examples/${exampleId}`, {
    method: "DELETE",
  });
}
export async function generateStyleProfile(
  clientId: number
): Promise<{ ok: true; styleProfile: string }> {
  return request(`/api/clients/${clientId}/style-profile/generate`, {
    method: "POST",
    body: "{}",
  });
}
export async function clearStyleProfile(clientId: number): Promise<void> {
  await request(`/api/clients/${clientId}/style-profile`, { method: "DELETE" });
}
export async function getStyleAccent(
  clientId: number
): Promise<{
  accent: string | null;
  // fix643: client's fonts for the styled-article preview (Google-Fonts-loaded
  // when recognizable). null when the client has no style_tokens.
  bodyFont?: string | null;
  headingFont?: string | null;
}> {
  return request(`/api/clients/${clientId}/style-accent`);
}
export async function setStyleAccent(
  clientId: number,
  accent: string | null
): Promise<{ ok: true; accent: string | null }> {
  return request(`/api/clients/${clientId}/style-accent`, {
    method: "POST",
    body: JSON.stringify({ accent }),
  });
}
// fix614: re-style + save every completed article for a client (picks up the
// current accent override / profile / fonts). Download All then ships them.
export async function styleAllArticles(
  clientId: number
): Promise<{ results: { id: number; ok: boolean; error?: string }[] }> {
  return request(`/api/articles/style-all`, {
    method: "POST",
    body: JSON.stringify({ clientId }),
  });
}
export async function scanStyleUrls(
  clientId: number,
  urls: string[]
): Promise<{
  ok: true;
  added: number;
  results: { url: string; ok: boolean; reason?: string }[];
}> {
  return request(`/api/clients/${clientId}/style-examples/scan`, {
    method: "POST",
    body: JSON.stringify({ urls }),
  });
}

// chunk3z-fix444: in-house crawl to discover current-site pages when there's no
// usable sitemap (or a third-party generator capped/missed pages).
export async function crawlSitemap(
  clientId: number,
  payload: { url?: string; replace?: boolean; launchDate?: string | null }
): Promise<{
  ok: boolean;
  imported: number;
  total: number;
  via: "sitemap" | "spider";
  locationScopedOut?: number;
  locationPrefix?: string | null;
}> {
  return request(`/api/clients/${clientId}/migration/crawl-sitemap`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

export async function setMigrationUrlJunk(
  urlId: number,
  isJunk: boolean
): Promise<{ ok: boolean }> {
  return request(`/api/migration-urls/${urlId}`, {
    method: "PATCH",
    body: JSON.stringify({ isJunk }),
  });
}

// fix622: cross-domain migration (old domain → new domain).
export interface OldSourceMatch {
  id: number | null;
  sourcePath: string;
  sourceUrl: string | null;
  targetUrl: string | null;
  overridden?: boolean;
  confidence: number;
  reason: string;
  needsReview: boolean;
  /** fix655: intentionally not redirected (e.g. a legal page with no equivalent). */
  skipped?: boolean;
}
export async function scanOldDomain(
  clientId: number,
  oldDomain: string
): Promise<{
  ok: boolean;
  oldDomain: string;
  found: number;
  usable: number;
  waybackCount: number;
  // fix648: breakdown so the UI can show where pages came from and flag a
  // silent Wayback failure (the "8 found vs 1,000 in archive" case).
  sitemapContributed?: number;
  waybackKept?: number;
  waybackError?: string | null;
}> {
  return request(`/api/clients/${clientId}/migration/scan-old-domain`, {
    method: "POST",
    body: JSON.stringify({ oldDomain }),
  });
}
export async function getOldSources(
  clientId: number
): Promise<{
  oldDomain: string | null;
  newDomain?: string;
  targets?: string[];
  sources: OldSourceMatch[];
}> {
  return request(`/api/clients/${clientId}/migration/old-sources`);
}
export function getOldDomainRedirectsUrl(
  clientId: number,
  format: "rewrite" | "location"
): string {
  return `/api/clients/${clientId}/migration/old-domain-redirects.conf?format=${format}`;
}
export async function patchOldSource(
  sourceId: number,
  payload: { removed?: boolean; targetOverride?: string | null; reviewed?: boolean; skipped?: boolean }
): Promise<{ ok: boolean }> {
  return request(`/api/migration-old-sources/${sourceId}`, {
    method: "PATCH",
    body: JSON.stringify(payload),
  });
}
export async function getTargetOptions(
  clientId: number
): Promise<{ targets: { url: string; path: string }[] }> {
  return request(`/api/clients/${clientId}/migration/target-options`);
}


export async function setMigrationUrlGeneration(
  urlId: number,
  generation: "gen1" | "gen2" | "gen3" | null
): Promise<{ ok: boolean }> {
  return request(`/api/migration-urls/${urlId}/generation`, {
    method: "PATCH",
    body: JSON.stringify({ generation }),
  });
}

/** chunk3z-fix388: re-derive generations without re-hitting archive.org —
 *  used after importing a sitemap or changing either boundary date. */
export async function recomputeGenerations(
  clientId: number,
  payload: { launchDate?: string | null; prevRelaunch?: string | null }
): Promise<{ ok: boolean; prevRelaunch: string | null; updated: number; livePaths: number }> {
  return request(`/api/clients/${clientId}/migration/recompute`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}

// chunk3z-fix395: redirect map + missing-pages report.
export interface MigrationRedirect {
  id: number;
  sourcePath: string;
  targetUrl: string | null;
  confidence: number | null;
  matchReason: string | null;
  status: string;
  /** chunk3z-fix422: the matcher guessed this one — confirm or correct it. */
  needsReview?: boolean;
}
export interface RedirectVerify {
  rows: number;
  duplicates: number;
  invalidTargets: number;
  liveSourceRows: number;
  ok: boolean;
  problems: string[];
}

export interface LocationAnalysis {
  isNamespaced: boolean;
  currentLocations: string[];
  oldLocations: string[];
  closedLocations: string[];
  genericPaths: string[];
}

export async function buildRedirects(
  clientId: number,
  opts?: { geoWords?: string[]; defaultLocation?: string | null }
): Promise<{
  ok: boolean;
  matched: number;
  unmatched: number;
  verify: RedirectVerify;
  locations: LocationAnalysis;
}> {
  return request(`/api/clients/${clientId}/migration/redirects`, {
    method: "POST",
    body: JSON.stringify(opts ?? {}),
  });
}

export async function getRedirects(
  clientId: number
): Promise<{ redirects: MigrationRedirect[]; verify: RedirectVerify | null }> {
  return request(`/api/clients/${clientId}/migration/redirects`);
}

export async function updateRedirect(
  rid: number,
  payload: { targetUrl?: string | null; status?: string; needsReview?: boolean }
): Promise<{ ok: boolean }> {
  return request(`/api/migration-redirects/${rid}`, {
    method: "PATCH",
    body: JSON.stringify(payload),
  });
}

// chunk3z-fix461: remove a redirect row entirely (won't show in the map or CSV).
export async function deleteRedirect(rid: number): Promise<{ ok: boolean }> {
  return request(`/api/migration-redirects/${rid}`, { method: "DELETE" });
}

export function getRedirectCsvUrl(clientId: number): string {
  return `/api/clients/${clientId}/migration/redirects.csv`;
}

export async function getMissingPages(clientId: number): Promise<{
  text: string;
  counts: { servicePages: number; blogPosts: number; other: number; notHundred: number };
  bands: { auto: number; review: number; manual: number };
}> {
  return request(`/api/clients/${clientId}/migration/missing-pages`);
}

// ===========================================================================
// chunk3z-fix424: Google Search Console
// ===========================================================================

export async function getGscStatus(): Promise<{
  connected: boolean;
  configured: boolean;
}> {
  return request(`/api/gsc/status`);
}

export async function getGscConnectUrl(): Promise<{ url: string }> {
  return request(`/api/gsc/connect`);
}

export async function disconnectGsc(): Promise<{ ok: boolean }> {
  return request(`/api/gsc/disconnect`, { method: "POST" });
}

export async function getGscProperties(): Promise<{
  properties: { siteUrl: string; permissionLevel: string }[];
}> {
  return request(`/api/gsc/properties`);
}

export async function importGsc(
  clientId: number,
  payload: { siteUrl?: string; pagesCsv?: string }
): Promise<{ ok: boolean; total: number; added: number; updated: number; sitemapAdded?: number }> {
  return request(`/api/clients/${clientId}/migration/gsc`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
}
